import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Box,
  Grid,
  Stack,
  Card,
  CardContent,
  Typography,
  IconButton,
} from "@mui/material";
import { Edit, Delete, Save } from "@mui/icons-material";
import { useSections } from "./SectionContext";
import ExtraHeader from "./ExtraHeader";

// Map section name → route
const sectionRoutes = {
  "Photo, Personal Details": "/PersonalDetail",
  "Websites, Portfolios, Profiles": "/WebsiteSection",
  Certifications: "/Certifications",
  Languages: "/Language",
  Accomplishments: "/AccomplishmentsSection",
  "Additional Information": "/AdditionalInfoForm",
  Software: "/SoftWare",
  References: "/Refferences",
  Volunteering: "/Volunteer",
  Interests: "/Interest",
};

const WebsitesSection = () => {
  const [links, setLinks] = useState([
    { url: "", addToHeader: false, placeholder: "github.com/yourusername", label: "GitHub_Link" },
    { url: "", addToHeader: false, placeholder: "linkedin.com/in/yourname", label: "LinkedIn_Link" },
    { url: "", addToHeader: false, placeholder: "yourportfolio.com", label: "Portfolio_Link" },
  ]);
  const [savedLinks, setSavedLinks] = useState([]);
  const navigate = useNavigate();
  const location = useLocation();
  const { sections } = useSections();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [selectedTheme, setSelectedTheme] = useState(null);

  // Load saved links
  useEffect(() => {
    const stored = localStorage.getItem("websites");
    if (stored) setSavedLinks(JSON.parse(stored));
  }, []);

  // Save to localStorage
  useEffect(() => {
    localStorage.setItem("websites", JSON.stringify(savedLinks));
  }, [savedLinks]);

  // Load selected template/theme from navigation
  useEffect(() => {
    if (location.state) {
      const { selectedTemplate: template, selectedTheme: theme } = location.state;
      if (template) setSelectedTemplate(template);
      if (theme) setSelectedTheme(theme);
    }
  }, [location.state]);

  const handleLinkChange = (index, value) => {
    const updated = [...links];
    updated[index].url = value;
    setLinks(updated);
  };

  const handleCheckboxChange = (index, checked) => {
    const updated = [...links];
    updated[index].addToHeader = checked;
    setLinks(updated);
  };

  const addNewLink = () => setLinks([...links, { url: "", addToHeader: false }]);

  // Combined Save & Next function
  const handleSaveAndNext = () => {
    const validLinks = links.filter((l) => l.url.trim() !== "");
    if (validLinks.length > 0) {
      setSavedLinks([...savedLinks, ...validLinks]);
      setLinks([{ url: "", addToHeader: false }]);
    }

    const currentSection = "Websites, Portfolios, Profiles";
    const currentIndex = sections.indexOf(currentSection);

    if (currentIndex < sections.length - 1) {
      const nextSection = sections[currentIndex + 1];
      navigate(sectionRoutes[nextSection], {
        state: { selectedTemplate, selectedTheme },
      });
    } else {
      navigate("/mainpage", {
        state: { selectedTemplate, selectedTheme },
      });
    }
  };

  const handleDelete = (index) => {
    const updated = [...savedLinks];
    updated.splice(index, 1);
    setSavedLinks(updated);
  };

  const handleEdit = (index) => {
    const linkToEdit = savedLinks[index];
    setLinks([linkToEdit]);
    handleDelete(index);
  };

  return (
    <Box sx={{ backgroundColor: "#EFF7FF", minHeight: "100vh" }}>

      {/* ✅ ExtraHeader replaces AppBar — Home icon on top right */}
      <ExtraHeader
        title="Websites, Portfolios, Profiles"
        onBack={() => navigate(-1)}
      />

      {/* Main Content */}
      <Box
        sx={{
          maxWidth: 600,
          mx: "auto",
          mt: 4,
          p: 3,
          backgroundColor: "#ffffff",
          borderRadius: 3,
          boxShadow: "0 4px 20px rgba(0, 43, 91, 0.12)",
          border: "1px solid #B3D9F7",
        }}
      >
        {/* Section Title */}
        <Typography
          variant="h6"
          sx={{ color: "#002B5B", fontWeight: "bold", mb: 2, borderBottom: "2px solid #0288D1", pb: 1 }}
        >
          Add Your Links
        </Typography>

        {/* Input Fields */}
        <Grid container spacing={2}>
          {links.map((link, index) => (
            <Grid item xs={12} key={index}>
              <Box display="flex" alignItems="center" gap={2}>
                <TextField
                  fullWidth
                  label={link.label || "Link / URL"}
                  placeholder={link.placeholder || "ex. linkedin.com/in/yourname"}
                  value={link.url}
                  onChange={(e) => handleLinkChange(index, e.target.value)}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": { borderColor: "#B3D9F7" },
                      "&:hover fieldset": { borderColor: "#0288D1" },
                      "&.Mui-focused fieldset": { borderColor: "#002B5B" },
                    },
                    "& .MuiInputLabel-root.Mui-focused": { color: "#002B5B" },
                  }}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={link.addToHeader}
                      onChange={(e) => handleCheckboxChange(index, e.target.checked)}
                      sx={{
                        color: "#0288D1",
                        "&.Mui-checked": { color: "#002B5B" },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" sx={{ color: "#002B5B", whiteSpace: "nowrap" }}>
                      Add to header
                    </Typography>
                  }
                />
              </Box>
            </Grid>
          ))}
        </Grid>

        <Button
          onClick={addNewLink}
          sx={{
            mt: 2,
            color: "#0288D1",
            textTransform: "none",
            fontWeight: "bold",
            "&:hover": { backgroundColor: "#E1F5FE", color: "#002B5B" },
          }}
        >
          + Add another link
        </Button>

        {/* Bottom Buttons */}
        <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
          <Stack direction="row" gap={2} sx={{ marginLeft: "auto" }}>
            <Button
              variant="contained"
              startIcon={<Save />}
              sx={{
                px: 6,
                borderRadius: 3,
                fontWeight: "bold",
                backgroundColor: "#002B5B",
                "&:hover": { backgroundColor: "#0288D1" },
                boxShadow: "0 3px 10px rgba(0, 43, 91, 0.3)",
              }}
              onClick={handleSaveAndNext}
            >
              Save & Next
            </Button>
          </Stack>
        </Stack>

        {/* Saved Links */}
        <Box sx={{ mt: 4 }}>
          {savedLinks.length > 0 && (
            <Typography
              variant="subtitle1"
              sx={{ color: "#002B5B", fontWeight: "bold", mb: 2, borderBottom: "2px solid #0288D1", pb: 1 }}
            >
              Saved Links
            </Typography>
          )}
          {savedLinks.map((link, index) => (
            <Card
              key={index}
              sx={{
                mb: 2,
                boxShadow: "0 2px 10px rgba(2, 136, 209, 0.15)",
                border: "1px solid #B3D9F7",
                borderLeft: "4px solid #002B5B",
                borderRadius: 2,
                "&:hover": { boxShadow: "0 4px 16px rgba(0, 43, 91, 0.2)" },
              }}
            >
              <CardContent
                sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
              >
                <Box>
                  {link.label && (
                    <Typography variant="subtitle2" sx={{ fontWeight: "bold", color: "#002B5B" }}>
                      {link.label}
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ color: "#0D4C8B" }}>{link.url}</Typography>
                  {link.addToHeader && (
                    <Typography variant="caption" sx={{ color: "#0288D1" }}>
                      (Added to header)
                    </Typography>
                  )}
                </Box>
                <Box>
                  <IconButton onClick={() => handleEdit(index)} sx={{ color: "#0288D1", "&:hover": { color: "#002B5B" } }}>
                    <Edit />
                  </IconButton>
                  <IconButton onClick={() => handleDelete(index)} color="error">
                    <Delete />
                  </IconButton>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default WebsitesSection;